import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { GlassWater, Check } from "lucide-react";

/**
 * FreeDrinkPicker — shown at the top of checkout. Every online order gets a
 * free drink; customer picks which one. Emits the selected drink name via
 * onChange(name) so CheckoutPage can send it as `free_drink` on the order.
 */
export default function FreeDrinkPicker({ value, onChange }) {
  const [drinks, setDrinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/menu")
      .then((r) => setDrinks(r.data.filter((m) => m.category === "Drinks" && m.available)))
      .catch(() => setDrinks([]))
      .finally(() => setLoading(false));
  }, []);

  const options = useMemo(() => drinks, [drinks]);

  if (loading) return null;
  if (options.length === 0) return null;

  return (
    <section
      data-testid="free-drink-picker"
      className="bg-emerald-50 border-2 border-emerald-500 rounded-xl overflow-hidden"
    >
      <div className="bg-emerald-600 text-white px-4 py-3 flex items-center gap-3">
        <GlassWater className="w-5 h-5 shrink-0" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest font-black">
            Your Free Drink
          </div>
          <div className="text-sm font-bold leading-tight">
            Free with every online order — pick one!
          </div>
        </div>
      </div>
      <div className="p-3 grid grid-cols-2 gap-2">
        {options.map((d) => {
          const active = value === d.name;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => onChange(active ? null : d.name)}
              data-testid={`free-drink-option-${d.id}`}
              className={`text-left rounded-lg px-3 py-2.5 border-2 transition-colors ${
                active
                  ? "border-emerald-600 bg-white shadow-sm"
                  : "border-stone-200 bg-white hover:border-emerald-400"
              }`}
            >
              <div className="flex items-center gap-2">
                <div
                  className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${
                    active
                      ? "border-emerald-600 bg-emerald-600"
                      : "border-stone-300"
                  }`}
                >
                  {active && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-stone-800 text-sm truncate">
                    {d.name}
                  </div>
                  {d.description && (
                    <div className="text-[10px] text-stone-500 truncate">
                      {d.description}
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
